import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { timeout, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { Environment, environments } from './configuration-view';

export interface SourceSession {
  token: string;
  email: string;
  userName: string;
}

interface LoginResponse {
  token: string;
  email: string;
  userName: string;
}

const storageKey = (source: Environment) => `source-token:${source}`;

function readSession(source: Environment): SourceSession | null {
  try {
    const raw = sessionStorage.getItem(storageKey(source));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SourceSession>;
    if (!parsed.token) return null;
    return { token: parsed.token, email: parsed.email ?? '', userName: parsed.userName ?? '' };
  } catch {
    return null;
  }
}

function readAll(): Record<Environment, SourceSession | null> {
  return {
    stable: readSession('stable'),
    alfa: readSession('alfa'),
    beta: readSession('beta'),
    prod: readSession('prod'),
  };
}

@Injectable({ providedIn: 'root' })
export class SourceAuthService {
  private readonly http = inject(HttpClient);
  readonly sessions = signal<Record<Environment, SourceSession | null>>(readAll());
  readonly complete = computed(() => environments.every(env => !!this.sessions()[env.id]?.token));

  login(source: Environment, username: string, password: string) {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/api/auth/${source}/login`, { username, password }).pipe(
      timeout(30_000),
      tap(response => {
        const session: SourceSession = {
          token: response.token,
          email: response.email ?? '',
          userName: response.userName ?? '',
        };
        sessionStorage.setItem(storageKey(source), JSON.stringify(session));
        this.sessions.update(all => ({ ...all, [source]: session }));
      }),
    );
  }

  logout(source: Environment): void {
    sessionStorage.removeItem(storageKey(source));
    this.sessions.update(all => ({ ...all, [source]: null }));
  }

  logoutAll(): void {
    for (const env of environments) this.logout(env.id);
  }

  tokenHeaders(sources: readonly Environment[]): Record<string, string> {
    const headers: Record<string, string> = {};
    const sessions = this.sessions();
    for (const source of sources) {
      const token = sessions[source]?.token;
      if (token) headers[`X-Token-${source}`] = token;
    }
    return headers;
  }
}
